import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Edit, Coins, Users, UserCheck, UserX, Key, UserPlus, Truck, Wrench, ClipboardCheck, Calculator, Cpu, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// App info for selection
const APPS = [
  { id: 'ridero', name: 'Ridero', icon: Truck, color: '#22c55e' },
  { id: 'mechanic_kiosk', name: 'MechanicKiosk', icon: Wrench, color: '#3b82f6' },
  { id: 'pti_pector', name: 'PTI Pector', icon: ClipboardCheck, color: '#f97316' },
  { id: 'mcanick_scout', name: 'Mcnk AI', icon: Cpu, color: '#8b5cf6' },
  { id: 'mr_mathematico', name: 'Mr.Mathematico', icon: Calculator, color: '#ec4899' },
  { id: 'fleet_share', name: 'Fleet Share', icon: Share2, color: '#06b6d4' },
];

export default function UsersPage() {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [userCount, setUserCount] = useState({ total: 0, active: 0 });
  
  // Edit user dialog
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  
  // Points dialog
  const [pointsUser, setPointsUser] = useState(null);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsDescription, setPointsDescription] = useState('');

  // Password change dialog
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  // Multi-app selection: { app_id: { enabled: bool, job_title: string } }
  const [newUserApps, setNewUserApps] = useState({
    ridero: { enabled: false, job_title: '' },
    mechanic_kiosk: { enabled: false, job_title: '' },
    pti_pector: { enabled: false, job_title: '' },
    mcanick_scout: { enabled: false, job_title: '' },
    mr_mathematico: { enabled: false, job_title: '' },
    fleet_share: { enabled: false, job_title: '' }
  });
  const [jobTitles, setJobTitles] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  const isMasterAdmin = currentUser?.role_id === 'master_admin';

  useEffect(() => {
    fetchData();
    fetchJobTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter]);

  const fetchJobTitles = async () => {
    try {
      const res = await axios.get(`${API}/job-titles`, { withCredentials: true });
      // API returns object like {app_id: [titles]}
      setJobTitles(res.data || {});
    } catch (e) {
      console.error('Failed to fetch job titles:', e);
      // Fallback to default titles
      setJobTitles({
        ridero: ["customer", "driver", "dispatcher", "admin"],
        mechanic_kiosk: ["mechanic", "driver", "parts_manager", "admin"],
        pti_pector: ["driver", "inspector", "safety_officer", "admin"],
        mcanick_scout: ["mechanic", "technician", "shop_manager", "trainer", "admin"],
        mr_mathematico: ["student", "teacher", "tutor", "admin"],
        fleet_share: ["user", "fleet_owner", "renter", "dispatcher", "admin"]
      });
    }
  };

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter && roleFilter !== 'all') params.append('role_id', roleFilter);

      const [usersRes, rolesRes, countRes] = await Promise.all([
        axios.get(`${API}/users?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/users/count`, { withCredentials: true })
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setUserCount(countRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    try {
      await axios.patch(`${API}/users/${editUser.user_id}`, {
        role_id: editRole
      }, { withCredentials: true });
      toast.success('User updated');
      setEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleAdjustPoints = async () => {
    if (!pointsUser || !pointsAmount || !pointsDescription) return;
    try {
      await axios.post(`${API}/points/${pointsUser.user_id}/adjust`, {
        amount: parseInt(pointsAmount),
        description: pointsDescription
      }, { withCredentials: true });
      toast.success('Points adjusted');
      setPointsUser(null);
      setPointsAmount('');
      setPointsDescription('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust points');
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditRole(user.role_id);
  };

  const openPointsDialog = (user) => {
    setPointsUser(user);
    setPointsAmount('');
    setPointsDescription('');
  };

  const openPasswordDialog = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = async () => {
    if (!passwordUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await axios.post(`${API}/users/${passwordUser.user_id}/change-password`, {
        new_password: newPassword
      }, { withCredentials: true });
      toast.success(`Password changed for ${passwordUser.email}`);
      setPasswordUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Build connected_apps and app_job_titles from multi-app selection
    const connected_apps = [];
    const app_job_titles = {};
    
    Object.entries(newUserApps).forEach(([appId, config]) => {
      if (config.enabled) {
        connected_apps.push(appId);
        if (config.job_title) {
          app_job_titles[appId] = config.job_title;
        }
      }
    });

    setIsCreating(true);
    try {
      const payload = {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role_id: newUserRole,
        connected_apps: connected_apps,
        app_job_titles: app_job_titles
      };

      await axios.post(`${API}/users/create`, payload, { withCredentials: true });
      toast.success(`User ${newUserName} created successfully!`);
      
      // Reset form
      resetCreateDialog();
      
      // Refresh user list
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserRole('user');
    setNewUserApps({
      ridero: { enabled: false, job_title: '' },
      mechanic_kiosk: { enabled: false, job_title: '' },
      pti_pector: { enabled: false, job_title: '' },
      mcanick_scout: { enabled: false, job_title: '' },
      mr_mathematico: { enabled: false, job_title: '' },
      fleet_share: { enabled: false, job_title: '' }
    });
  };

  const toggleAppAccess = (appId) => {
    setNewUserApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        enabled: !prev[appId].enabled,
        job_title: !prev[appId].enabled ? prev[appId].job_title : '' // Clear job title when disabling
      }
    }));
  };

  const setAppJobTitle = (appId, title) => {
    setNewUserApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        job_title: title
      }
    }));
  };

  return (
    <DashboardLayout>
      <Header title="Users" subtitle="Manage all users across connected apps" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="total-users-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{userCount.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="active-users-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{userCount.active}</p>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="inactive-users-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                <UserX className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{userCount.total - userCount.active}</p>
                <p className="text-sm text-muted-foreground">Inactive Users</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="users-search-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="role-filter-select">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.role_id} value={role.role_id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isMasterAdmin && (
                <Button 
                  onClick={() => setShowCreateDialog(true)} 
                  className="gap-2"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Create User
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">All Users</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Connected Apps</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.user_id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`role-badge role-${user.role_id} text-xs`}>
                          {user.role_id?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">{user.points_balance?.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.connected_apps?.length > 0 ? (
                            user.connected_apps.map((app) => (
                              <Badge key={app} variant="outline" className="text-xs">
                                {app}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.user_id}`}
                            title="Edit Role"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openPointsDialog(user)}
                            data-testid={`adjust-points-${user.user_id}`}
                            title="Adjust Points"
                          >
                            <Coins className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openPasswordDialog(user)}
                            data-testid={`change-password-${user.user_id}`}
                            title="Change Password"
                            className="text-orange-500 hover:text-orange-600"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update role for {editUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.role_id} value={role.role_id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleUpdateUser} data-testid="save-user-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog */}
      <Dialog open={!!pointsUser} onOpenChange={() => setPointsUser(null)}>
        <DialogContent data-testid="adjust-points-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Points</DialogTitle>
            <DialogDescription>
              Adjust points for {pointsUser?.name} (Current: {pointsUser?.points_balance})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (use negative to deduct)</Label>
              <Input
                type="number"
                placeholder="e.g. 100 or -50"
                value={pointsAmount}
                onChange={(e) => setPointsAmount(e.target.value)}
                data-testid="points-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Reason for adjustment"
                value={pointsDescription}
                onChange={(e) => setPointsDescription(e.target.value)}
                data-testid="points-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPointsUser(null)}>Cancel</Button>
            <Button onClick={handleAdjustPoints} data-testid="save-points-btn">Adjust Points</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!passwordUser} onOpenChange={() => setPasswordUser(null)}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-500" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{passwordUser?.email}</strong>
              <br />
              <span className="text-xs text-muted-foreground">
                This will affect login across ALL connected apps (Ridero, MechanicKiosk, PTI Pector)
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
              />
            </div>
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-500">Passwords do not match</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordUser(null)}>Cancel</Button>
            <Button 
              onClick={handleChangePassword} 
              data-testid="save-password-btn"
              disabled={!newPassword || newPassword !== confirmPassword || newPassword.length < 6}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={resetCreateDialog}>
        <DialogContent className="max-w-lg" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Create New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account for the Hub and assign them to an app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="John Smith"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  data-testid="new-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  data-testid="new-user-email-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  data-testid="new-user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hub Role</Label>
                <Select value={newUserRole} onValueChange={setNewUserRole}>
                  <SelectTrigger data-testid="new-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {roles.map((role) => (
                      <SelectItem key={role.role_id} value={role.role_id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* App Access Permissions */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-semibold text-muted-foreground">APP ACCESS PERMISSIONS</Label>
              <p className="text-xs text-muted-foreground mb-3">Select which apps this user can access via SSO login</p>
              
              <div className="space-y-3">
                {APPS.map((app) => {
                  const Icon = app.icon;
                  const isEnabled = newUserApps[app.id]?.enabled;
                  return (
                    <div 
                      key={app.id} 
                      className={`p-3 rounded-xl border-2 transition-all ${
                        isEnabled 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-border/50 bg-secondary/20 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => toggleAppAccess(app.id)}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                              isEnabled ? 'bg-green-500/30' : 'bg-secondary/50'
                            }`}
                            data-testid={`toggle-app-${app.id}`}
                          >
                            <Icon className="w-5 h-5" style={{ color: isEnabled ? app.color : '#666' }} />
                          </button>
                          <div>
                            <p className={`font-medium ${isEnabled ? '' : 'text-muted-foreground'}`}>{app.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {isEnabled ? '✓ Can login via SSO' : 'No access'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Job Title Select - only show when enabled */}
                          {isEnabled && (
                            <Select 
                              value={newUserApps[app.id]?.job_title || ''} 
                              onValueChange={(val) => setAppJobTitle(app.id, val)}
                            >
                              <SelectTrigger className="w-36" data-testid={`jobtitle-${app.id}`}>
                                <SelectValue placeholder="Job title..." />
                              </SelectTrigger>
                              <SelectContent>
                                {jobTitles[app.id]?.map((title) => (
                                  <SelectItem key={title} value={title}>
                                    {title.charAt(0).toUpperCase() + title.slice(1).replace('_', ' ')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          
                          {/* Toggle Switch */}
                          <button
                            type="button"
                            onClick={() => toggleAppAccess(app.id)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              isEnabled ? 'bg-green-500' : 'bg-secondary'
                            }`}
                            data-testid={`switch-${app.id}`}
                          >
                            <div 
                              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                isEnabled ? 'translate-x-7' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            {newUserName && (
              <div className="bg-secondary/30 rounded-lg p-3 mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">PREVIEW</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                    {newUserName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium">{newUserName || 'New User'}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="capitalize">{newUserRole.replace('_', ' ')}</span>
                      {Object.entries(newUserApps)
                        .filter(([_, config]) => config.enabled)
                        .map(([appId, config]) => (
                          <span key={appId} className="flex items-center gap-1">
                            <span>•</span>
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ borderColor: APPS.find(a => a.id === appId)?.color }}
                            >
                              {APPS.find(a => a.id === appId)?.name}
                              {config.job_title && ` (${config.job_title})`}
                            </Badge>
                          </span>
                        ))
                      }
                      {!Object.values(newUserApps).some(c => c.enabled) && (
                        <span className="text-yellow-500">• No app access</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCreateDialog}>Cancel</Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={isCreating || !newUserName || !newUserEmail || !newUserPassword || newUserPassword.length < 6}
              className="bg-green-600 hover:bg-green-700 gap-2"
              data-testid="submit-create-user-btn"
            >
              {isCreating ? 'Creating...' : <><UserPlus className="w-4 h-4" /> Create User</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
